import {useSuspenseQuery} from '@tanstack/react-query'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {memo, useMemo} from 'react'
import clsx from 'clsx'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
} from '~/utils/product'
import {getPriceFromPriceString} from '~/client/spreadshop/old/hacks'
import {defaultImagesQueryOptions} from '~/client/fn/defaultImages'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import useShopConfig from '~/hooks/useShopConfig'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {ImageFormat} from '~/gql/graphql'

type UpsellingProduct = {
    product: {
        id: string
        vId: string
        name: string
        productTypeId: string
        productTypeName: string
        defaultAppearanceId?: string
        defaultViewId?: string
        price: string
    }
    appearance?: string
    _key: string
}

type UpsellingProductsProps = {
    className?: string
    products: UpsellingProduct[]
    onSelect?: () => void
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
}

export const UpsellingProducts = ({className, products, onSelect}: UpsellingProductsProps) => {
    const params = useLocaleParams()
    const shopConfig = useShopConfig()
    const {data: defaultImages} = useSuspenseQuery(defaultImagesQueryOptions(params.locale))
    const productsWithImage = useMemo(() => {
        return products.map((item) => {
            const productTypeImage = defaultImages.find(
                (image) => image.productTypeId === item.product.productTypeId
            )?.images

            const baseProductImageSrc = `${shopConfig.imageServerEndpoint}/products/${item.product.vId}`
            const baseDesignImageSrc = getCompositionImageBaseUrl(baseProductImageSrc)!

            const appearanceId = item.appearance ?? item.product.defaultAppearanceId
            const viewId = item.product.defaultViewId ?? '1'

            return {
                name: item.product.name,
                price: getPriceFromPriceString(item.product.price),
                productId: item.product.id,
                productTypeName: item.product.productTypeName,
                appearanceId: appearanceId,
                image: {
                    src: getProductModelImageSrc(
                        baseProductImageSrc,
                        viewId,
                        appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    srcset: getProductModelImageSrcSet(
                        baseProductImageSrc,
                        viewId,
                        appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    altText: item.product.name,
                    format: ImageFormat.Png,
                },
                designImage: {
                    src: getDesignImageSrc(baseDesignImageSrc, viewId, appearanceId),
                    srcset: getDesignImageSrcSet(baseDesignImageSrc, viewId, appearanceId),
                    altText: item.product.name,
                },
            }
        })
    }, [products, defaultImages, shopConfig.imageServerEndpoint])

    return (
        <div className={clsx('relative', className)}>
            <Carousel config={config} showDots={false} initialSlidesToShow={3.25}>
                {productsWithImage.map((item, index) => (
                    <Link
                        key={item.productId}
                        className="block h-full group outline-none"
                        to="/$lang/products/$productId"
                        params={{...params, productId: item.productId}}
                        search={{
                            appearanceId: item.appearanceId,
                        }}
                        onClick={onSelect}
                    >
                        <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-purple-600 transition duration-200 ease-in-out transform-gpu">
                            <ImageComposition
                                className="overflow-hidden"
                                imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                lazy={index > 3}
                                image={item.image}
                                sizes="500px"
                            />
                            <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                <ImageComposition
                                    className="rounded-md overflow-hidden bg-white"
                                    image={item?.designImage!}
                                    lazy={true}
                                    sizes="500px"
                                />
                            </div>
                            <div className="flex gap-4 flex-row justify-between mx-4 mt-4 mb-2 text-sm flex-shrink-0">
                                <div className="truncate text-gray-400">{item.name}</div>
                                <Price className="text-nowrap font-medium">
                                    {item.price?.vatIncluded}
                                </Price>
                            </div>
                            <div className="mx-4  mb-4 line-clamp-1 text-sm flex-grow">
                                {item.productTypeName}
                            </div>
                        </Card>
                    </Link>
                ))}
            </Carousel>
        </div>
    )
}

export const UpsellingProductsSkeletonLoader = memo(() => {
    return (
        <div>
            <Carousel
                config={{...config, all: {...config.all, draggable: false}}}
                showDots={false}
                showArrows={false}
            >
                {[...new Array(8)].map((_, index) => (
                    <Card key={index} className="p-4">
                        <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                        <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                    </Card>
                ))}
            </Carousel>
        </div>
    )
})
